import type { CcsBufferRuleSet } from "./types.js";

// ccs-buffer ruleset for FY2025-26 (1 July 2025 – 30 June 2026).
//
// This module is a forward-projection layer over the SHIPPED au13 CCS rate
// engine: it holds ONLY the withholding / balancing constants and a pointer to
// the au13 FY whose income taper / rate caps it reuses. The CCS percentage taper
// is NEVER duplicated here — au13 owns it (see calculate.ts).
//
// Figures are transcribed from the verification ledger
// (ccs-withholding-buffer-optimiser-figures.md). All four mechanics figures land
// AGENT_VERIFIED — quoted near-verbatim from the Services Australia primary pages
// as surfaced in search and independently corroborated; the low CITED count is
// an artefact of primary-host unreachability, not weak evidence (no figure
// failed). Refresh AGENT_VERIFIED → CITED at the P4 pre-ship review.
//
//   - Default withholding buffer: 5% of CCS held back, released at balancing if
//     no debt (figure 1). Families may set 0–100%.
//   - Change withholding % online: up to TWICE a year via myGov; more often by
//     phoning the Families line (figure 2).
//   - Debt recovery: 20% of ongoing CCS payments withheld until the debt clears,
//     if CCS is current (figure 3).
//   - Income-confirmation window: 2 years after FY end, after which CCS/ACCS is
//     cancelled and the overpayment becomes a debt / Account Payable Notice
//     (figure 4).
//
// The per-family "typical debt" dollar (figure 6) is UNVERIFIED — NOT encoded
// here, and never presented as a precise/average dollar (copy.ts surfaces it
// only as a qualitative "common outcome" band).
//
// Never edit a shipped ruleset to "fix" a later year — author a new fyXXXX-XX.ts
// (and re-point au13Fy / bump constants) when the FY rolls over.
export const fy2025_26: CcsBufferRuleSet = {
  product: "SHARED",
  fy: "2025-26",
  effectiveFrom: "2025-07-01",
  effectiveTo: "2026-06-30",
  au13Fy: "2025-26",
  sources: {
    balancing:
      "https://www.servicesaustralia.gov.au/balancing-child-care-subsidy?context=41186",
    changeWithholding:
      "https://www.servicesaustralia.gov.au/centrelink-online-account-help-change-your-withholding-percentage-for-child-care-subsidy",
    balancingOutcome:
      "https://www.servicesaustralia.gov.au/understanding-your-child-care-subsidy-balancing-outcome?context=41186",
    timeLimits:
      "https://www.servicesaustralia.gov.au/time-limits-for-confirming-income-for-child-care-subsidy?context=41186",
    incomeThresholds: "https://guides.dss.gov.au/family-assistance-guide/1/1/i/70",
  },
  sourcesVerifiedAt: "2026-06-22",

  // Figure 1 — Services Australia withholds 5% of CCS by default.
  defaultWithholdingRate: 0.05,
  // Figure 3 — 20% of ongoing CCS payments withheld to recover a debt.
  debtRecoveryRate: 0.2,
  // Figure 2 — up to twice a year online (more often by phone).
  onlineWithholdingChangesPerYear: 2,
  // Figure 4 — 2 years to confirm income before CCS cancels + debt arises.
  incomeConfirmationWindowYears: 2,
};
